import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    View, Text, StyleSheet, TouchableOpacity,
    Animated, Modal, Dimensions, StatusBar, AppState
} from 'react-native';
import { Video } from 'expo-av';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

const { width: SW, height: SH } = Dimensions.get('window');

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtMs = (ms) => {
    if (!ms && ms !== 0) return '0:00';
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
};

// ── ShortClipPlayer ──────────────────────────────────────────────────────────
/**
 * A premium inline video player for short clips.
 *
 * Features:
 *  - Muted autoplay-ready (tap to play/pause)
 *  - Animated pulse icon on tap
 *  - Slim purple progress bar at the bottom edge
 *  - Remaining-time badge (bottom-left)
 *  - Mute/unmute button (bottom-right)
 *  - Fullscreen button (top-right) → full-screen Modal
 *  - Graceful gradient scrim
 */
const ShortClipPlayer = ({ uri, poster, width, height, borderRadius = 12, autoPlay = true }) => {
    const videoRef = useRef(null);
    const fsVideoRef = useRef(null);

    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);
    const [durationMs, setDurationMs] = useState(null);
    const [positionMs, setPositionMs] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const loopCount = useRef(0);

    // Animations
    const flashScale = useRef(new Animated.Value(0)).current;
    const flashOpacity = useRef(new Animated.Value(0)).current;
    const progressAnim = useRef(new Animated.Value(0)).current;

    const [appState, setAppState] = useState(AppState.currentState);

    // Sync playing state with visibility and app focus
    useEffect(() => {
        if (!autoPlay || appState !== 'active') {
            setPlaying(false);
        } else {
            // Reset state when coming back into view
            loopCount.current = 0;
            setPlaying(true);
            videoRef.current?.setPositionAsync(0);
        }
    }, [autoPlay, appState]);

    // Explicitly handle playback to avoid "stuck" states
    useEffect(() => {
        if (playing) {
            videoRef.current?.playAsync();
        } else {
            videoRef.current?.pauseAsync();
        }
    }, [playing]);

    useEffect(() => {
        const subscription = AppState.addEventListener('change', nextAppState => {
            setAppState(nextAppState);
        });
        return () => subscription.remove();
    }, []);

    // ── Playback status callback ──────────────────────────────────────────
    const onStatus = useCallback((status) => {
        if (!status.isLoaded) {
            if (isLoaded) setIsLoaded(false);
            return;
        }
        if (!isLoaded) setIsLoaded(true);
        if (status.durationMillis) setDurationMs(status.durationMillis);
        if (status.positionMillis !== undefined) {
            setPositionMs(status.positionMillis);
            if (status.durationMillis) {
                progressAnim.setValue(status.positionMillis / status.durationMillis);
            }
        }

        if (status.didJustFinish) {
            loopCount.current += 1;
            if (loopCount.current < 3) {
                videoRef.current?.setPositionAsync(0);
            } else {
                setPlaying(false);
                loopCount.current = 0;
            }
        }
    }, [progressAnim, isLoaded]);

    // ── Animated flash icon on tap ────────────────────────────────────────
    const flashIcon = () => {
        flashScale.setValue(0.6);
        flashOpacity.setValue(0.9);
        Animated.parallel([
            Animated.spring(flashScale, { toValue: 1.2, useNativeDriver: true, speed: 25, bounciness: 6 }),
            Animated.sequence([
                Animated.delay(500),
                Animated.timing(flashOpacity, { toValue: 0, duration: 250, useNativeDriver: true }),
            ]),
        ]).start();
    };

    const togglePlay = () => {
        if (!playing) loopCount.current = 0;
        setPlaying(p => !p);
        flashIcon();
    };

    const openFullscreen = () => {
        setFullscreen(true);
    };

    // ── Progress bar width ────────────────────────────────────────────────
    const progressWidth = progressAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['0%', '100%'],
    });

    const remaining = durationMs != null ? durationMs - positionMs : null;

    // ── Inner player (reused for inline + fullscreen) ─────────────────────
    const renderPlayer = (playerUri, playerW, playerH, ref, isFull = false) => (
        <View
            style={{
                width: playerW,
                height: playerH,
                borderRadius: isFull ? 0 : borderRadius,
                overflow: 'hidden',
                backgroundColor: '#000',
            }}
        >
            <Video
                ref={ref}
                source={{ uri: playerUri }}
                style={{ width: playerW, height: playerH }}
                resizeMode="cover"
                shouldPlay={playing}
                isLooping={false}
                isMuted={muted}
                volume={1.0}
                onPlaybackStatusUpdate={isFull ? undefined : onStatus}
            />

            {/* Poster image (shown until video loads or when paused/stopped) */}
            {poster && (!isLoaded || !playing) && (
                <Animated.Image
                    source={{ uri: poster }}
                    style={[StyleSheet.absoluteFill, { borderRadius: isFull ? 0 : borderRadius }]}
                    resizeMode="cover"
                />
            )}

            {/* Tap to play/pause */}
            <TouchableOpacity
                style={StyleSheet.absoluteFillObject}
                activeOpacity={1}
                onPress={togglePlay}
            />

            {/* Bottom gradient scrim */}
            <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.55)']}
                style={p.scrim}
                pointerEvents="none"
            />

            {/* Play/Pause flash icon */}
            <Animated.View
                pointerEvents="none"
                style={[p.flashWrap, { opacity: flashOpacity, transform: [{ scale: flashScale }] }]}
            >
                <View style={p.flashOrb}>
                    <Ionicons name={playing ? 'pause' : 'play'} size={36} color="#fff" />
                </View>
            </Animated.View>

            {/* Manual Play Overlay (only if not playing and not loaded/finised) */}
            {!playing && isLoaded && (
                <View style={p.pauseOverlay} pointerEvents="none">
                    <View style={p.pauseOrb}>
                        <Ionicons name="play" size={32} color="#fff" style={{ marginLeft: 4 }} />
                    </View>
                </View>
            )}

            {/* Top-right controls row */}
            <View style={p.topRight}>
                {/* Mute toggle */}
                <TouchableOpacity onPress={() => setMuted(m => !m)} activeOpacity={0.75}>
                    <View style={p.ctrlBtn}>
                        <Ionicons
                            name={muted ? 'volume-mute' : 'volume-high'}
                            size={14}
                            color="#fff"
                        />
                    </View>
                </TouchableOpacity>

                {/* Fullscreen (inline only) */}
                {!isFull && (
                    <TouchableOpacity onPress={openFullscreen} activeOpacity={0.75}>
                        <View style={p.ctrlBtn}>
                            <Ionicons name="expand" size={13} color="#fff" />
                        </View>
                    </TouchableOpacity>
                )}

                {/* Exit fullscreen */}
                {isFull && (
                    <TouchableOpacity onPress={() => setFullscreen(false)} activeOpacity={0.75}>
                        <View style={p.ctrlBtn}>
                            <Ionicons name="contract" size={14} color="#fff" />
                        </View>
                    </TouchableOpacity>
                )}
            </View>

            {/* Bottom-left: remaining time + clip badge */}
            <View style={p.bottomLeft}>
                <View style={p.timeBadge}>
                    <Ionicons name="film" size={9} color="#a78bfa" />
                    <Text style={p.timeText}>
                        {remaining != null ? fmtMs(remaining) : '—'}
                    </Text>
                </View>
            </View>

            {/* Bottom-right: duration total */}
            {durationMs && (
                <View style={p.bottomRight}>
                    <Text style={p.totalDuration}>{fmtMs(durationMs)}</Text>
                </View>
            )}

            {/* Slim progress bar — bottom edge */}
            <View style={p.progressTrack} pointerEvents="none">
                <Animated.View style={[p.progressFill, { width: progressWidth }]} />
            </View>
        </View>
    );

    return (
        <>
            {renderPlayer(uri, width, height, videoRef, false)}

            {/* Fullscreen modal */}
            <Modal
                visible={fullscreen}
                transparent={false}
                animationType="fade"
                statusBarTranslucent
                onRequestClose={() => setFullscreen(false)}
            >
                <View style={p.fsContainer}>
                    <StatusBar hidden />
                    {renderPlayer(uri, SW, SH, fsVideoRef, true)}
                </View>
            </Modal>
        </>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const p = StyleSheet.create({
    scrim: {
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: '45%',
        pointerEvents: 'none',
    },

    // Flash overlay
    flashWrap: {
        position: 'absolute',
        alignSelf: 'center',
        top: 0, bottom: 0, left: 0, right: 0,
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    flashOrb: {
        width: 68, height: 68, borderRadius: 34,
        backgroundColor: 'rgba(0,0,0,0.5)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.2)',
    },

    // Control buttons (top-right)
    topRight: {
        position: 'absolute',
        top: 10, right: 10,
        flexDirection: 'row', gap: 8,
    },
    ctrlBtn: {
        width: 30, height: 30, borderRadius: 15,
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)',
    },

    // Bottom-left
    bottomLeft: {
        position: 'absolute', bottom: 12, left: 10,
        flexDirection: 'row', alignItems: 'center', gap: 6,
    },
    timeBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(0,0,0,0.55)',
        borderRadius: 8, paddingHorizontal: 7, paddingVertical: 3,
    },
    timeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

    // Bottom-right
    bottomRight: {
        position: 'absolute', bottom: 12, right: 10,
    },
    totalDuration: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 10, fontWeight: '600',
    },

    // Progress bar
    progressTrack: {
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 3, backgroundColor: 'rgba(255,255,255,0.15)',
    },
    progressFill: {
        height: 3,
        backgroundColor: '#7c3aed',
        borderRadius: 1.5,
    },

    // Fullscreen
    fsContainer: { flex: 1, backgroundColor: '#000' },
    
    // Pause overlay
    pauseOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.2)',
    },
    pauseOrb: {
        width: 64, height: 64, borderRadius: 32,
        backgroundColor: 'rgba(0,0,0,0.4)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)',
    },
});

export default ShortClipPlayer;
